import React, { useCallback } from "react";
import { Calendar, Clock, CheckCircle, XCircle, Sparkles } from "lucide-react";
import { ApiService } from "../../../services/api";
import { AppointmentItem } from "../../../types/appointment";
import DynamicList from "../../../components/dynamic/List";

export default function ClientViewMyAppointments() {
  const handleAIPrompt = () => {
    window.dispatchEvent(new CustomEvent("toggleAIPanel"));
    setTimeout(() => {
      const message = "Yaklaşan randevularım neler?";
      window.dispatchEvent(new CustomEvent("sendAIMessage", { detail: { message } }));
    }, 300);
  };

  const fetchMyAppointments = useCallback(async (page: number, searchTerm?: string) => {
    const res = await ApiService.getMyBookings({
      sortBy: "startDate",
      sortOrder: "DESC",
      limit: 20,
      page,
      search: searchTerm || undefined,
    });

    const anyRes: any = res as any;
    const meta = anyRes?.meta || {};
    const pageNum = meta.page ?? anyRes.page ?? page ?? 1;
    const limitNum = meta.limit ?? anyRes.limit ?? 20;
    const totalItemsNum = meta.totalItems ?? anyRes.totalItems ?? (Array.isArray(anyRes?.data) ? anyRes.data.length : 0);
    const totalPagesNum = meta.totalPages ?? anyRes.totalPages ?? Math.max(1, Math.ceil((totalItemsNum || 0) / (limitNum || 1)));

    return {
      data: anyRes?.data ?? [],
      meta: {
        page: pageNum,
        totalPages: totalPagesNum,
        totalItems: totalItemsNum,
        limit: limitNum,
      },
    };
  }, []);

  const renderAppointmentItem = useCallback((apt: AppointmentItem) => {
    return (
      <div key={apt.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-all duration-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div>
                <h3 className="text-gray-900 font-semibold text-sm sm:text-base">
                  {`${apt.provider?.firstName ?? ""} ${apt.provider?.lastName ?? ""}`.trim() || apt.title || "Randevu"}
                </h3>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
              <span className="flex items-center space-x-1">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>{new Date(apt.startDate).toLocaleDateString()} </span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>
                  {new Date(apt.startDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {" - "}
                  {new Date(apt.endDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </span>
            </div>
            {apt.description && <p className="text-gray-600 text-xs sm:text-sm italic">"{apt.description}"</p>}
          </div>

          <div
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg font-medium flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm flex-shrink-0 ${
              apt.status === "APPROVED"
                ? "bg-green-100 text-green-800 border border-green-200"
                : apt.status === "PENDING"
                ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                : apt.status === "CANCELLED"
                ? "bg-gray-100 text-gray-800 border border-gray-300"
                : "bg-red-100 text-red-800 border border-red-200"
            }`}>
            {apt.status === "APPROVED" ? (
              <>
                <CheckCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Onaylandı</span>
              </>
            ) : apt.status === "PENDING" ? (
              <>
                <Clock size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Bekliyor</span>
              </>
            ) : apt.status === "CANCELLED" ? (
              <>
                <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>İptal Edildi</span>
              </>
            ) : (
              <>
                <XCircle size={16} className="sm:w-[18px] sm:h-[18px]" />
                <span>Reddedildi</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }, []);

  const aiButton = (
    <button
      onClick={handleAIPrompt}
      className="inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 border border-purple-600 flex-shrink-0 transition-colors">
      <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      <span>AI'ya Sor</span>
    </button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col h-full">
      <DynamicList
        embedded
        type="list"
        header={false}
        title="Randevularım"
        actions={aiButton}
        isSearch={true}
        isPagination={true}
        path=""
        fetcher={fetchMyAppointments}
        emptyMessage="Henüz randevunuz bulunmuyor"
        item={renderAppointmentItem}
      />
    </div>
  );
}
